import { useState, useRef, useEffect } from "react";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  DeleteIcon,
  TimeIcon,
  RepeatIcon,
  ExternalLinkIcon
} from "@chakra-ui/icons";
import {
  HStack,
  IconButton,
  VStack,
  Card,
  CardBody,
  CardHeader,
  Badge,
  Button,
  Collapse,
  useDisclosure,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Text,
  Spinner,
  Box,
  Tooltip,
  Link,
  Switch,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import { epochConverter } from "utils/dateUtils";
import { Header } from "build-ts/metadata";
import {
  JobRunStatus,
  ScheduledJobProto,
  ScheduledJobRunProto,
  ScheduledJobType,
} from "build-ts/schedule";
import { MetadataTypeEnum, metadataTypeEnumToJSON } from "build-ts/enums";
import { getScheduledJobRun, setScheduledJobRunVisibility } from "services/ScheduleServices";
import showToast from "hooks/useCustomToast";
import InsightsCard from "./InsightsCard";
import LogsSection from "./LogsSection";
import { useUser } from "context/UserContext";

// Individual job run card component
interface JobRunCardProps {
  scheduledJob: ScheduledJobProto;
  jobRun: Header;
  scheduledJobType: ScheduledJobType;
  onDelete: (runId: string) => void;
}

const JobRunCard: React.FC<JobRunCardProps> = ({
  scheduledJob,
  jobRun,
  scheduledJobType,
  onDelete,
}) => {
  const [runDetails, setRunDetails] = useState<ScheduledJobRunProto | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTogglingVisibility, setIsTogglingVisibility] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const deleteDialog = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const { defaultTimeZone } = useUser();

  // Helper function to get status color and label
  const getStatusInfo = (status: JobRunStatus) => {
    switch (status) {
      case JobRunStatus.PENDING:
        return { color: "gray", label: "Pending" };
      case JobRunStatus.IN_PROGRESS:
        return { color: "blue", label: "In Progress" };
      case JobRunStatus.SUCCESS:
        return { color: "green", label: "Success" };
      case JobRunStatus.FAILURE:
        return { color: "red", label: "Failed" };
      case JobRunStatus.PARTIAL_SUCCESS:
        return { color: "orange", label: "Partial Success" };
      default:
        return { color: "gray", label: "Unknown" };
    }
  };

  useEffect(()=>{
    const fetchRunDetails = async () => {
      setIsLoading(true);
      const details = await getScheduledJobRun(jobRun?.id || "");
      if (details) {
        setRunDetails(details);
        setIsVisible(details?.visibility || false);
      } else {
        setRunDetails(null);
      }
      setIsLoading(false);
    };
    fetchRunDetails();
  },[jobRun?.id]);

  // Get run details when expanded
  const handleToggleExpand = async () => {
    setIsExpanded(!isExpanded);
  };

  // Refresh individual run details
  const handleRefreshRun = async () => {
    if (!jobRun.id) return;

    setIsRefreshing(true);
    const details = await getScheduledJobRun(jobRun.id);
    if (details) {
      setRunDetails(details);
      setIsVisible(details?.visibility || false);
      showToast("Run status refreshed successfully", "", "success");
    } else {
      showToast("Failed to refresh run status", "", "error");
    }
    setIsRefreshing(false);
  };

  // Handle visibility toggle
  const handleVisibilityToggle = async (newVisibility: boolean) => {
    if (!jobRun.id) return;

    setIsTogglingVisibility(true);
    const response = await setScheduledJobRunVisibility(jobRun.id, newVisibility);
    
    if (response) {
      // Update the local visibility state
      setIsVisible(newVisibility);
      showToast(
        `Job run ${newVisibility ? "shown" : "hidden"} successfully`,
        "",
        "success"
      );
    } else {
      showToast("Failed to update job run visibility", "", "error");
    }
    setIsTogglingVisibility(false);
  };

  const handleDelete = () => {
    if (jobRun.id) {
      onDelete(jobRun.id);
    }
    deleteDialog.onClose();
  };

  const statusInfo = runDetails
    ? getStatusInfo(runDetails?.status || JobRunStatus.PENDING)
    : null;

  return (
    <>
      <Card size="sm" variant="outline">
        <CardHeader pb={1}>
          <HStack justify="space-between" align="center">
            <VStack align="start" spacing={1} flex={1}>
              <HStack spacing={1} color="gray.500" align="center" justify="space-between">
                <TimeIcon boxSize={3} />
                <Text fontSize="sm" fontWeight="semibold" mb={0} pr={2}>
                  {jobRun?.created_date_ms
                    ? epochConverter(jobRun?.created_date_ms) + " " + defaultTimeZone
                    : "N/A"}
                </Text>
                {runDetails?.status && (
                  <Badge colorScheme={statusInfo?.color} size="xs" mr={2}>
                    {statusInfo?.label}
                  </Badge>
                )}
              
              </HStack>
            </VStack>

            <HStack spacing={0}>
              {/* Visibility Toggle */}
              <HStack spacing={1} align="center" justify="space-between" mr={5}>
              <Text fontSize="xs" color="gray.600" fontWeight="semibold" mb={0} ml={1}>
                  {isVisible ? "Visible" : "Hidden"}
                </Text>
                <Switch
                  id="visibility-switch"
                  size="sm"
                  colorScheme="purple"
                  isChecked={isVisible}
                  onChange={(e) => handleVisibilityToggle(e.target.checked)}
                  isDisabled={isTogglingVisibility}
                  ml={2}
                  mt={2}
                />
              </HStack>
              <Tooltip label="Open">
                {scheduledJobType === ScheduledJobType.PLAN_EXECUTION ? (
                  <IconButton
                    aria-label="Open run"
                    icon={<ExternalLinkIcon />}
                    size="sm"
                    variant="ghost"
                    colorScheme="gray"
                    onClick={()=>{
                      window.open(`/analyze?metadataId=${runDetails?.plan_run?.tree_header?.id}&metadataType=${metadataTypeEnumToJSON(MetadataTypeEnum.ANALYSIS_TREE)}`, "_blank");
                    }}
                  />
                ) : (
                  <IconButton
                    aria-label="Open run"
                    icon={<ExternalLinkIcon />}
                    size="sm"
                    variant="ghost"
                    colorScheme="gray"
                    onClick={()=>{
                      const metadataType = metadataTypeEnumToJSON(MetadataTypeEnum.CUSTOM_AGENT);
                      window.open(`/analyze?metadataId=${scheduledJob?.header?.id}&runId=${jobRun?.id}&metadataType=${metadataType}`, "_blank");
                    }}
                  />
                )}
              </Tooltip>
              <Tooltip label="Refresh Run Status">
                <IconButton
                  aria-label="Refresh run status"
                  icon={<RepeatIcon />}
                  size="sm"
                  variant="ghost"
                  colorScheme="gray"
                  onClick={handleRefreshRun}
                  isLoading={isRefreshing}
                />
              </Tooltip>
              <Tooltip label="Delete Run">
                <IconButton
                  aria-label="Delete run"
                  icon={<DeleteIcon />}
                  size="sm"
                  variant="ghost"
                  colorScheme="red"
                  onClick={deleteDialog.onOpen}
                />
              </Tooltip>
              <IconButton
                aria-label={isExpanded ? "Collapse" : "Expand"}
                icon={isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
                size="sm"
                variant="ghost"
                onClick={handleToggleExpand}
              />
            </HStack>
          </HStack>
        </CardHeader>

        <Collapse in={isExpanded}>
          <CardBody pt={0}>
            {isLoading ? (
              <HStack justify="center" py={4}>
                <Spinner size="sm" />
                <Text fontSize="sm">Loading run details...</Text>
              </HStack>
            ) : runDetails ? (
              <VStack spacing={0} align="stretch">
                {/* Insights based on run type */}
                {scheduledJobType === ScheduledJobType.PLAN_EXECUTION &&
                  runDetails?.plan_run && (
                    <Box>
                      <Text fontWeight="semibold" mb={2} fontSize="sm">
                        Plan Execution Results:{" "}
                        <Badge colorScheme={statusInfo?.color} size="xs">
                          {statusInfo?.label}
                        </Badge>
                      </Text>
                      {runDetails?.plan_run?.tree_header && (
                        <Text fontSize="sm" mb={0}>
                          <strong>Analysis Tree:</strong>{" "}
                          <Link
                            as={RouterLink}
                            to={`/analyze?metadataId=${runDetails.plan_run.tree_header.id}&metadataType=${metadataTypeEnumToJSON(MetadataTypeEnum.ANALYSIS_TREE)}`}
                            color="purple.600"
                            textDecoration="underline"
                            _hover={{
                              color: "purple.700",
                              textDecoration: "none",
                            }}
                            isExternal
                          >
                            {runDetails?.plan_run?.tree_header?.name}
                          </Link>
                        </Text>
                      )}
                      <InsightsCard insights={runDetails?.plan_run?.insights} />
                    </Box>
                  )}

                {scheduledJobType === ScheduledJobType.QNA_EXECUTION &&
                  runDetails?.agent_run && (
                    <Box>
                      <InsightsCard agentOutput={runDetails?.agent_run} />
                    </Box>
                  )}

                {/* Run Logs */}
                {runDetails?.run_logs && runDetails?.run_logs.length > 0 && (
                  <LogsSection logs={runDetails?.run_logs} />
                )}
              </VStack>
            ) : (
              <Text fontSize="sm" color="gray.500">
                Failed to load run details
              </Text>
            )}
          </CardBody>
        </Collapse>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={deleteDialog.isOpen}
        leastDestructiveRef={cancelRef}
        onClose={deleteDialog.onClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Job Run
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to delete this job run? This action cannot
              be undone.
              <strong>Run:</strong> {jobRun?.name || "Unknown Run"}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={deleteDialog.onClose}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={handleDelete} ml={3}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default JobRunCard;
